<template>
    <!-- 規格 11：沒有文字就不顯示（`NONE` 不渲染任何東西） -->
    <button v-if="state !== AUDIO.NONE" type="button" class="apb"
        :class="[`apb--${state}`, { 'apb--on': playing }]"
        :disabled="disabled" :title="tip" @click.stop="$emit('click')">
        <!-- 規格 3：外圈進度環。⚠ 用 SVG 而不是 CSS conic-gradient——
             conic-gradient 在 Safari 14 之前不支援，而老師的機器不見得新。 -->
        <svg v-if="playing" class="apb-ring" viewBox="0 0 28 28" aria-hidden="true">
            <circle class="apb-ring-bg" cx="14" cy="14" :r="R" fill="none" />
            <circle class="apb-ring-fg" cx="14" cy="14" :r="R" fill="none"
                :stroke-dasharray="C" :stroke-dashoffset="C * (1 - progress)" />
        </svg>
        <v-icon class="apb-icon" :size="iconSize">{{ icon }}</v-icon>
        <!-- 規格 9：右上小驚嘆號角標 -->
        <span v-if="warn" class="apb-badge" aria-hidden="true">!</span>
    </button>
</template>

<script>
import { AUDIO, isPlayable } from './videoQuestUtils';

/// 進度環的半徑。28px 直徑、2px 線寬 → 半徑 13 會被切掉一半，所以取 12。
const R = 12;

/**
 * 一句語音的播放鍵（VF8 規格 1–11）。
 *
 * ⚠⚠ **這顆鍵不自己播放。** 它只回報「被點了」，實際的 `Audio` 實例由
 * `VideoQuestView` 單獨持有——規格第 5 點要「一次只播一個」，
 * 每顆鍵各自 `new Audio()` 正是「按了新的、舊的還在響」的成因。
 */
export default {
    name: 'AudioPlayBtn',
    props: {
        /// `videoQuestUtils.AUDIO` 的其中一個值。
        state: { type: String, required: true },
        /// 這一句正在播（由父層的單一播放狀態決定，不是自己的）。
        playing: { type: Boolean, default: false },
        /// 0–1。規格 3 的進度環用，依音檔長度換算。
        progress: { type: Number, default: 0 },
        /**
         * 播放中顯示 ■（停止）而不是 ❙❙（暫停）。
         *
         * ⚠ 依序試聽用這個：點下去是**整串停掉**、不是暫停這一句，
         * 畫 ❙❙ 會讓人以為再按一下會從原處接著播。
         */
        stopIcon: { type: Boolean, default: false },
        /// 蓋掉預設的 tooltip（依序試聽要說的是「依序試聽這一題」）。
        label: { type: String, default: '' },
    },
    data() {
        return { AUDIO, R, C: 2 * Math.PI * R };
    },
    computed: {
        icon() {
            if (this.playing) { return this.stopIcon ? 'mdi-stop' : 'mdi-pause'; }
            if (this.state === AUDIO.BUSY) { return 'mdi-loading'; }
            if (this.state === AUDIO.FAILED) { return 'mdi-refresh'; }
            return 'mdi-play';
        },
        /// ⚠ ◌ 與 ↻ 比 ▶ 視覺上大一點，縮一級才不會撐破 28px。
        iconSize() {
            return this.state === AUDIO.BUSY || this.state === AUDIO.FAILED ? 14 : 16;
        },
        /// 規格 9：待更新與未產生都掛角標。
        warn() {
            return this.state === AUDIO.STALE || this.state === AUDIO.MISSING;
        },
        /**
         * 規格 8：產生中不可點。
         * ⚠ `MISSING` 也不可點——它有角標看起來像 `STALE`，但**沒有舊檔可播**，
         * 讓它可以按只會得到一個播不出聲音的鍵。
         */
        disabled() {
            return this.state === AUDIO.BUSY || this.state === AUDIO.MISSING;
        },
        tip() {
            const key = {
                [AUDIO.READY]: 'play',
                [AUDIO.BUSY]: 'busy',
                [AUDIO.STALE]: 'stale',
                [AUDIO.MISSING]: 'missing',
                [AUDIO.FAILED]: 'failed',
            }[this.state];
            if (this.playing) {
                return this.$t(this.stopIcon ? 'unit.vq.audio.seq_stop' : 'unit.vq.audio.pause');
            }
            return this.label || this.$t(`unit.vq.audio.${key}`);
        },
    },
    methods: { isPlayable },
};
</script>

<style scoped>
/* 規格 2：28px 圓形，與欄位基線對齊 */
.apb {
    position: relative;
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(0, 0, 0, .22);
    background: #fff;
    color: rgba(0, 0, 0, .74);
    cursor: pointer;
    transition: background .15s, border-color .15s, color .15s;
}
.apb:hover:not(:disabled) { background: rgba(0, 0, 0, .05); }
.apb:disabled { cursor: default; opacity: .55; }

/* 規格 7：可播放是實心 ▶ */
.apb--ready { border-color: rgba(0, 0, 0, .3); }

/* 規格 9：待更新／未產生 → 警示邊框 */
.apb--stale, .apb--missing {
    border-color: #f0a03a;
    color: #b06a10;
    background: #fffaf2;
}
/* 規格 10：失敗 */
.apb--failed {
    border-color: #e0654f;
    color: #c0392b;
    background: #fff6f4;
}
/* 播放中 */
.apb--on { background: #1976d2; border-color: #1976d2; color: #fff; }

.apb-icon { color: inherit !important; }
/* 規格 8：產生中的 ◌ 要轉，否則看不出是「進行中」 */
.apb--busy .apb-icon { animation: apb-spin 1.1s linear infinite; }
@keyframes apb-spin { to { transform: rotate(360deg); } }

/* 規格 3：進度環 */
.apb-ring {
    position: absolute;
    top: -1px; left: -1px;
    width: 28px; height: 28px;
    /* ⚠ 從 12 點鐘方向開始，不然環會從 3 點鐘開始跑 */
    transform: rotate(-90deg);
    pointer-events: none;
}
.apb-ring-bg { stroke: rgba(255, 255, 255, .35); stroke-width: 2; }
.apb-ring-fg {
    stroke: #fff;
    stroke-width: 2;
    stroke-linecap: round;
    /* ⚠ 不加 transition：timeupdate 大約每 250ms 一次，
       補間反而會讓環落後聲音，看起來像不同步 */
}

/* 規格 9：右上角小驚嘆號 */
.apb-badge {
    position: absolute;
    top: -3px; right: -3px;
    min-width: 13px; height: 13px;
    padding: 0 2px;
    border-radius: 7px;
    background: #f0a03a;
    color: #fff;
    font-size: 9px;
    font-weight: 700;
    line-height: 13px;
    text-align: center;
}
</style>
